import MoreHorizIcon from '@mui/icons-material/MoreHoriz';
import { Box, Tooltip } from '@mui/material';
import {
  getGridDateOperators,
  getGridStringOperators,
  GridActionsCellItem,
  GridColDef,
} from '@mui/x-data-grid-pro';

import { IcLockFilled, IcUnlock } from '@/assets/images';
import { useUserStore } from '@/store';
import { formatPersonName } from '@/utils';

interface DIRColActions {
  onOpenMenu?: (event: React.MouseEvent<HTMLButtonElement>, row: any) => void;
}

const ALLOWED_DATE_OPERATORS = ['after', 'before', 'isEmpty'];

export const domesticIncidentReportCols = (
  actions?: DIRColActions,
): Record<string, () => Partial<GridColDef>> => ({
  dirId: () => ({
    field: 'dirId',
    headerName: 'ID',
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
    renderCell: (params) => {
      const { user } = useUserStore();
      const isCaseRestricted = params.row.isRestricted;
      const caseRestrictedByUser = params.row.caseRestrictedByUser;
      const usersWithAccess = params.row.usersWithCaseAccessPopulated || [];

      const userHasAccess = user
        ? usersWithAccess.some((u: { _id: string }) => u._id === user._id)
        : false;

      const getUserDisplayName = (u: {
        firstName?: string;
        lastName?: string;
        email?: string;
      }) => (u.firstName || u.lastName ? formatPersonName(u) : u.email || '');

      const restrictedByName = caseRestrictedByUser
        ? getUserDisplayName(caseRestrictedByUser)
        : '';

      const allowedUserNames = usersWithAccess
        .map((u: { firstName?: string; lastName?: string; email?: string }) =>
          getUserDisplayName(u),
        )
        .join('\n');

      const tooltipTitle = isCaseRestricted
        ? userHasAccess
          ? `Restricted by ${restrictedByName} \nAllowed users:\n${allowedUserNames}`
          : `Restricted by ${restrictedByName}`
        : '';

      const RestrictionIcon = isCaseRestricted
        ? userHasAccess
          ? IcUnlock
          : IcLockFilled
        : null;

      return (
        <Box display="flex" alignItems="center" gap={0.5}>
          <span>{params.value}</span>
          {RestrictionIcon && (
            <Tooltip title={tooltipTitle}>
              <RestrictionIcon style={{ width: 14, height: 14 }} />
            </Tooltip>
          )}
        </Box>
      );
    },
  }),
  agency: () => ({
    field: 'agency',
    headerName: 'Agency',
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  incidentNumber: () => ({
    field: 'incidentNumber',
    headerName: 'Incident number',
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  reportedDate: () => ({
    field: 'reportedDate',
    headerName: 'Reported date',
    type: 'dateTime',
    filterOperators: getGridDateOperators(true).filter((op) =>
      ALLOWED_DATE_OPERATORS.includes(op.value),
    ),
    valueGetter: (value: string) => (value ? new Date(value) : null),
    valueFormatter: (value: Date | string | null) => {
      if (!value) return '';
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
  }),
  occurredDate: () => ({
    field: 'occurredDate',
    headerName: 'Occurred date',
    type: 'dateTime',
    filterOperators: getGridDateOperators(true).filter((op) =>
      ALLOWED_DATE_OPERATORS.includes(op.value),
    ),
    valueGetter: (value: string) => (value ? new Date(value) : null),
    valueFormatter: (value: Date | string | null) => {
      if (!value) return '';
      const date = value instanceof Date ? value : new Date(value);
      if (isNaN(date.getTime())) return '';
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      const year = date.getFullYear();
      return `${month}/${day}/${year}`;
    },
  }),
  complaint: () => ({
    field: 'complaint',
    headerName: 'Complaint number',
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  address: () => ({
    field: 'address',
    headerName: 'Address',
    filterOperators: getGridStringOperators().filter(
      (item) => item.value !== 'isAnyOf',
    ),
  }),
  actions: () => ({
    field: 'actions',
    type: 'actions',
    headerName: 'Actions',
    align: 'center',
    headerAlign: 'center',
    getActions: (params) => [
      <GridActionsCellItem
        key={`menu-${params.row.id}`}
        icon={<MoreHorizIcon />}
        label="Menu"
        onClick={(event) =>
          actions?.onOpenMenu?.(
            event as React.MouseEvent<HTMLButtonElement>,
            params.row,
          )
        }
        color="primary"
      />,
    ],
  }),
});
